/**
 * Shared validation utilities
 * Note: For complex validation schemas, consider using Zod and exporting schemas here
 */

/**
 * Validate email format
 * @param email - Email to validate
 * @returns True if valid email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Minimum password length enforced across every password-creation flow.
 */
export const PASSWORD_MIN_LENGTH = 12

export type PasswordPolicyError =
  | "too_short"
  | "missing_lowercase"
  | "missing_uppercase"
  | "missing_digit"
  | "missing_symbol"

/**
 * Validate a password against the platform password policy.
 * Returns the first failing rule code, or null when the password is strong.
 * Mirrors Supabase's `lower_upper_letters_digits_symbols` requirement.
 */
export function validatePassword(password: string): PasswordPolicyError | null {
  if (password.length < PASSWORD_MIN_LENGTH) return "too_short"
  if (!/[a-z]/.test(password)) return "missing_lowercase"
  if (!/[A-Z]/.test(password)) return "missing_uppercase"
  if (!/\d/.test(password)) return "missing_digit"
  if (!/[^A-Za-z0-9]/.test(password)) return "missing_symbol"
  return null
}

/**
 * Validate URL format
 * @param url - URL to validate
 * @returns True if valid URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Validate coordinates
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns True if valid coordinates
 */
export function isValidCoordinates(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
}

/**
 * Validate positive number
 * @param value - Number to validate
 * @returns True if positive number
 */
export function isPositiveNumber(value: number): boolean {
  return typeof value === "number" && value > 0 && !isNaN(value)
}

/**
 * Validate non-negative number
 * @param value - Number to validate
 * @returns True if non-negative number
 */
export function isNonNegativeNumber(value: number): boolean {
  return typeof value === "number" && value >= 0 && !isNaN(value)
}

/**
 * Validate string is not empty
 * @param value - String to validate
 * @returns True if non-empty string
 */
export function isNonEmptyString(value: string): boolean {
  return typeof value === "string" && value.trim().length > 0
}

/**
 * Validate country code (ISO 3166-1 alpha-2)
 * @param code - Country code to validate
 * @returns True if valid 2-letter country code format
 */
export function isValidCountryCode(code: string): boolean {
  return /^[A-Z]{2}$/.test(code)
}

/**
 * Sanitize filename (remove dangerous characters)
 * @param filename - Filename to sanitize
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .trim()
    .replace(/[/\\]/g, "_")
    .replace(/\.\./g, "_")
    .replace(/[^\w\s.-]/g, "_")
    .replace(/\s+/g, "-")
    .slice(0, 120)
}

/**
 * Validate file size
 * @param size - File size in bytes
 * @param maxSize - Maximum allowed size in bytes
 * @returns True if file size is within limit
 */
export function isValidFileSize(size: number, maxSize: number): boolean {
  return size <= maxSize
}

/**
 * Validate file extension
 * @param filename - Filename to check
 * @param allowedExtensions - Array of allowed extensions (e.g., ['.jpg', '.png'])
 * @returns True if file extension is allowed
 */
export function isValidFileExtension(
  filename: string,
  allowedExtensions: string[],
): boolean {
  const ext = filename
    .substring(filename.lastIndexOf("."))
    .toLowerCase()
  return allowedExtensions.includes(ext)
}

/**
 * Validate MIME type
 * @param mimeType - MIME type to check
 * @param allowedMimes - Array of allowed MIME types
 * @returns True if MIME type is allowed
 */
export function isValidMimeType(
  mimeType: string,
  allowedMimes: string[],
): boolean {
  return allowedMimes.includes(mimeType)
}

/** One byte run that must match at a given offset. A signature is a list of parts that must ALL match (AND); a type maps to a list of alternative signatures (OR). */
type SignaturePart = { offset: number; bytes: number[] }

/**
 * Known file magic byte signatures.
 * Used to verify actual file content matches declared type.
 */
const FILE_SIGNATURES: Record<string, SignaturePart[][]> = {
  "application/pdf": [[{ offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }]], // %PDF
  "image/jpeg": [[{ offset: 0, bytes: [0xFF, 0xD8, 0xFF] }]],
  "image/png": [[{ offset: 0, bytes: [0x89, 0x50, 0x4E, 0x47] }]],
  "image/heic": [[{ offset: 0, bytes: [0x00, 0x00, 0x00] }]], // ftyp box (partial — HEIC is complex)
  "image/tiff": [[{ offset: 0, bytes: [0x49, 0x49, 0x2A, 0x00] }], [{ offset: 0, bytes: [0x4D, 0x4D, 0x00, 0x2A] }]], // Little/Big endian
  "image/gif": [[{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61] }], [{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61] }]], // GIF87a / GIF89a
  // WEBP is a RIFF container: "RIFF" at 0, a 4-byte size field, then the "WEBP" FourCC at
  // offset 8. Checking both parts (not just the RIFF prefix) actually validates WEBP content
  // instead of accepting any RIFF file (e.g. a renamed .wav/.avi) with the right extension/MIME.
  "image/webp": [[{ offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] }, { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] }]],
  // ZIP local file header — also covers DOCX/XLSX and KMZ, which are ZIP archives.
  "application/zip": [[{ offset: 0, bytes: [0x50, 0x4B, 0x03, 0x04] }]],
  "application/vnd.google-earth.kmz": [[{ offset: 0, bytes: [0x50, 0x4B, 0x03, 0x04] }]],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [[{ offset: 0, bytes: [0x50, 0x4B, 0x03, 0x04] }]],
}

const SIGNATURE_READ_BYTES = 16 // covers every signature part's offset+length above (WEBP's FourCC ends at 12)

/**
 * Validate file content by checking magic bytes against known signatures.
 * Falls back to true if the MIME type has no known signature.
 */
export async function validateFileMagicBytes(
  file: File,
  declaredMime: string,
): Promise<boolean> {
  const signatures = FILE_SIGNATURES[declaredMime]
  if (!signatures) return true // No known signature to check

  const header = new Uint8Array(await file.slice(0, SIGNATURE_READ_BYTES).arrayBuffer())

  return signatures.some((sig) =>
    sig.every((part) => part.bytes.every((byte, i) => header[part.offset + i] === byte)),
  )
}

/**
 * Validate date range
 * @param start - Start date
 * @param end - End date
 * @returns True if valid date range (end after start)
 */
export function isValidDateRange(start: Date, end: Date): boolean {
  return start < end
}

/**
 * Sanitize a redirect path from user input to prevent open-redirect attacks.
 * Only allows relative paths starting with "/".
 * Rejects absolute URLs, protocol-relative URLs (//evil.com), and non-path strings.
 * @param input - The raw redirect path from query params
 * @param fallback - Default path if input is unsafe
 */
export function safeRedirectPath(input: string | null | undefined, fallback: string): string {
  if (!input) return fallback
  // Reject ASCII control characters (tab/CR/LF/etc). The WHATWG URL parser
  // strips these before scheme resolution, so "/\n/evil.com" passes the
  // "/"-prefix and no-backslash checks below yet still resolves off-origin —
  // confirmed: new URL("/\n/evil.com", base).origin is the attacker's origin.
  if (/[\x00-\x1f\x7f]/.test(input)) return fallback
  // Must start with exactly one "/" (not "//")
  if (!input.startsWith("/") || input.startsWith("//")) return fallback
  // Block encoded backslash tricks
  if (input.includes("\\")) return fallback
  // Backstop: resolve against a fixed sentinel origin and confirm it actually
  // stayed there. Catches any other parser-normalization trick the checks
  // above don't anticipate, without depending on the caller's real origin.
  const SENTINEL_ORIGIN = "https://safe-redirect.internal"
  try {
    if (new URL(input, SENTINEL_ORIGIN).origin !== SENTINEL_ORIGIN) return fallback
  } catch {
    return fallback
  }
  return input
}
